import type { Card } from './collection.ts';
import { parseTypeLine } from './filing.ts';

// The physical drawers: two catalogs of six, each with labelled dividers.
// Dividers are listed front to back, as they stand in the drawer. Drawer
// ids match the locations table (migrations 0001 and 0004).

const BY_TYPE = ['Creature', 'Enchantment', 'Instant', 'Sorcery', 'Artifact'];
/** Reverse WUBRG: green at the front, white at the back. */
const BY_COLOR = ['Green', 'Red', 'Black', 'Blue', 'White'];

export const DRAWERS = [
  { id: 'catalog1-drawer1', label: 'White', dividers: BY_TYPE },
  { id: 'catalog1-drawer2', label: 'Blue spells', dividers: ['Sorcery', 'Instant'] },
  { id: 'catalog1-drawer3', label: 'Blue permanents', dividers: ['Creature', 'Enchantment', 'Artifact'] },
  { id: 'catalog1-drawer4', label: 'Red', dividers: BY_TYPE },
  { id: 'catalog1-drawer5', label: 'Green', dividers: BY_TYPE },
  { id: 'catalog1-drawer6', label: 'Black', dividers: BY_TYPE },
  { id: 'catalog2-drawer1', label: 'Artifacts', dividers: ['Creature', 'Non-creature'] },
  { id: 'catalog2-drawer2', label: 'Lands', dividers: ['Color restricted', 'Fits any deck'] },
  { id: 'catalog2-drawer3', label: 'Multicolor', dividers: BY_TYPE },
  { id: 'catalog2-drawer4', label: 'Mono-colored legendaries', dividers: BY_COLOR },
  { id: 'catalog2-drawer5', label: 'Multicolored legendaries', dividers: ['Two colors', 'Three colors', 'Four+ colors'] },
  { id: 'catalog2-drawer6', label: 'Basic lands', dividers: BY_COLOR },
] as const;

export type DrawerId = (typeof DRAWERS)[number]['id'];

/** Where a card files: a drawer and a divider in it, or no spot (yet) and why. */
export type Spot = { drawerId: DrawerId; divider: string } | { drawerId: null; reason: string };

const COLOR_NAME: Record<string, string> = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };
const MONO_DRAWER: Record<string, DrawerId> = { W: 'catalog1-drawer1', R: 'catalog1-drawer4', G: 'catalog1-drawer5', B: 'catalog1-drawer6' };

/** Creature beats every other type; battles file behind Enchantment. */
function typeDivider(types: string[]): string {
  if (types.includes('Creature')) return 'Creature';
  if (types.includes('Enchantment') || types.includes('Battle')) return 'Enchantment';
  if (types.includes('Instant')) return 'Instant';
  if (types.includes('Sorcery')) return 'Sorcery';
  if (types.includes('Artifact')) return 'Artifact';
  return 'Other';
}

type Fileable = Pick<Card, 'typeLine' | 'colors' | 'colorIdentity' | 'producedMana'>;

/**
 * The filing rules, checked in order:
 * 1. Basic lands: the basic lands drawer, by the color they make.
 * 2. Other lands: the lands drawer, by whether their color identity limits
 *    which Commander decks they can go in (Reflecting Pool makes any color but
 *    fits any deck; Watery Grave needs a blue-black commander).
 * 3. Colorless artifacts (legendary or not): the artifacts drawer. Colorless
 *    non-artifact cards have no spot yet.
 * 4. Colored legendary creatures and planeswalkers: the legendary drawers.
 * 5. Everything else: its color's drawer (multicolor has its own; blue is
 *    split into spells and permanents), behind its type's divider.
 */
export function fileCard(card: Fileable): Spot {
  const { supertypes, types } = parseTypeLine(card.typeLine);
  const colors = card.colors;

  if (types.includes('Land')) {
    const makes = card.producedMana.filter((m) => m in COLOR_NAME);
    if (supertypes.includes('Basic')) {
      if (makes.length === 1) return { drawerId: 'catalog2-drawer6', divider: COLOR_NAME[makes[0]!]! };
      return { drawerId: null, reason: 'Basic lands that make no colored mana (Wastes) have no divider yet' };
    }
    return { drawerId: 'catalog2-drawer2', divider: card.colorIdentity.length ? 'Color restricted' : 'Fits any deck' };
  }

  if (colors.length === 0) {
    if (types.includes('Artifact')) return { drawerId: 'catalog2-drawer1', divider: types.includes('Creature') ? 'Creature' : 'Non-creature' };
    return { drawerId: null, reason: 'Colorless cards that aren’t artifacts have no spot yet' };
  }

  if (supertypes.includes('Legendary') && (types.includes('Creature') || types.includes('Planeswalker'))) {
    if (colors.length === 1) return { drawerId: 'catalog2-drawer4', divider: COLOR_NAME[colors[0]!]! };
    return { drawerId: 'catalog2-drawer5', divider: colors.length === 2 ? 'Two colors' : colors.length === 3 ? 'Three colors' : 'Four+ colors' };
  }

  const divider = typeDivider(types);
  if (colors.length > 1) return { drawerId: 'catalog2-drawer3', divider };
  if (colors[0] === 'U') {
    return { drawerId: divider === 'Sorcery' || divider === 'Instant' ? 'catalog1-drawer2' : 'catalog1-drawer3', divider };
  }
  return { drawerId: MONO_DRAWER[colors[0]!]!, divider };
}

export const drawerById = (id: string) => DRAWERS.find((d) => d.id === id);

/** Position for sorting: drawer, then divider (front to back). Cards with no spot sort last. */
export function spotRank(spot: Spot): [number, number] {
  if (!spot.drawerId) return [DRAWERS.length, 0];
  const index = DRAWERS.findIndex((d) => d.id === spot.drawerId);
  const dividers: readonly string[] = DRAWERS[index]!.dividers;
  const divider = dividers.indexOf(spot.divider);
  return [index, divider === -1 ? dividers.length : divider];
}

/** "Catalog 1 / Drawer 2 · Sorcery", or the reason there's no spot. */
export function spotLabel(spot: Spot, drawerName: (id: string) => string = defaultDrawerName): string {
  return spot.drawerId ? `${drawerName(spot.drawerId)} · ${spot.divider}` : spot.reason;
}

const defaultDrawerName = (id: string) => id.replace(/catalog(\d)-drawer(\d)/, 'Catalog $1 / Drawer $2');

type Sortable = Fileable & Pick<Card, 'name' | 'setCode' | 'collectorNumber'>;

/** Physical filing order: drawer, divider (front to back), then name. */
export function compareFiling(a: Sortable, b: Sortable): number {
  const [da, va] = spotRank(fileCard(a));
  const [db, vb] = spotRank(fileCard(b));
  return (
    da - db ||
    va - vb ||
    a.name.localeCompare(b.name) ||
    a.setCode.localeCompare(b.setCode) ||
    a.collectorNumber.localeCompare(b.collectorNumber, 'en', { numeric: true })
  );
}
